// StepControllerContext.tsx
import React, {
    createContext, useCallback, useContext, useReducer,
} from 'react';
import { stepControllerReducer } from './stepControllerReducer';
import { StepControllerContextType, SetCurrentStepSubmissionArgsProps } from './RouterBasedStepper.types';

const StepControllerContext = createContext<StepControllerContextType | undefined>(undefined);

const initialState = {
    currentSubmit: null,
    isContinueDisabled: false,
    isContinueLoading: false,
    isBackDisabled: false,
    isStepCompleted: false,
    pageErrors: null,
};

export const useStepController = () => {
    const ctx = useContext(StepControllerContext);

    if (!ctx) throw new Error('useStepController must be used within StepControllerProvider');

    return ctx;
};

export const StepControllerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(stepControllerReducer, initialState);

    const setCurrentStepController = useCallback(({
        callbackOnContinue, isContinueDisabled = false, isContinueLoading = false, isBackDisabled = false,
    }: SetCurrentStepSubmissionArgsProps) => {
        dispatch({
            type: 'setCurrentSubmit',
            payload: {
                fn: callbackOnContinue, isContinueDisabled, isContinueLoading, isBackDisabled,
            },
        });
    }, [dispatch]);

    const setStepComplete = (val) => {
        dispatch({ type: 'setStepComplete', payload: val });
    };

    const setPageErrors = (val) => {
        dispatch({ type: 'setPageErrors', payload: val });
    };

    const setContinueDisabled = (disabled: boolean) => {
        dispatch({ type: 'setContinueDisabled', payload: disabled });
    };

    const handleOnContinueCallback = useCallback(() => {
        if (state?.currentSubmit) {
            state.currentSubmit();
        }
    }, [state?.currentSubmit]);

    return (
        <StepControllerContext.Provider
            value={{
                ...state,
                setCurrentStepController,
                setStepComplete,
                setPageErrors,
                setContinueDisabled,
                handleOnContinueCallback,
            }}
        >
            {children}
        </StepControllerContext.Provider>
    );
};
